import type { Category } from '@scayle/storefront-nuxt'
import type { SbMainNavigation } from '~/cms/storyblok/types/storyblok.d.ts'
import useRouteHelpers from '../composables/useRouteHelpers'

/**
 * Resolves the raw (unlocalized) link target for a category: its CMS page path
 * (`countryLevelCustomData.cmsPagePath`) when set, otherwise the category path.
 *
 * This is the single source of truth for the "a category can be replaced by a
 * content page" rule, shared by navigation (`linkCategoryTo`) and breadcrumb
 * builders (ECOM-2384) so the two can never drift.
 */
export const categoryLinkPath = (category: Category): string => {
  const { path, countryLevelCustomData } = category
  const cmsPagePath = countryLevelCustomData?.cmsPagePath

  return cmsPagePath ? (cmsPagePath as string) : path
}

/**
 * Returns custom path to CMS page (if set) instead of category path
 */
export const linkCategoryTo = (category: Category) => {
  const { toLocalePath } = useRouteHelpers()

  return toLocalePath(categoryLinkPath(category))
}

/**
 * Checks if a category is a sale category based on either:
 * 1. Category properties containing 'sale'
 * 2. Storyblok label being 'Sale'
 */
export function isSaleCategory(category: Category | SbMainNavigation): boolean {
  if (!category) return false

  // Check if it's a Storyblok navigation item with label
  if ('label' in category) {
    return category.label?.toLowerCase() === 'sale'
  }

  // Check Scayle category properties
  if ('properties' in category && Array.isArray(category.properties)) {
    return category.properties?.some((p) => p.name === 'sale') || false
  }

  return false
}

/**
 * Check whether given category is a root category
 */
export const isRootCategory = (category: Category): boolean => {
  return category.parentId === 0
}

/**
 * Recursively find a category by id anywhere in a category tree (root or not).
 */
export const findCategoryById = (
  categories: Category[],
  id: string,
): Category | undefined => {
  for (const category of categories) {
    if (category.id.toString() === id) {
      return category
    }
    if (category.children?.length) {
      const match = findCategoryById(category.children, id)
      if (match) return match
    }
  }
  return undefined
}

export const isHiddenForPLPNavigation = (category: Category) =>
  category?.shopLevelCustomData?.HideForPLPNavigation ||
  category?.shopLevelCustomData?.HasProducts === false

export const isHiddenForMainNavigation = (category: Category) =>
  category?.shopLevelCustomData?.HideForMainNavigation ||
  category?.shopLevelCustomData?.HasProducts === false

export const shouldShowHoverImagesImmediately = (category: Category) =>
  category?.shopLevelCustomData?.PLPShowHoverImages || false

export const getAllCategoryPaths = (categories: Category[]): string[] => {
  return categories
    .map(({ children, path }: Category) => {
      return children ? [path, ...getAllCategoryPaths(children)] : path
    })
    .flat()
}
